/**
 * Blog article related-content / products rail — carousel or marquee
 * (layout mechanics from grid/carousel/marquee reference).
 */
import React, { useRef } from "react";
import { updateRefClick } from "../../../Utils/SupabaseServices/ContentDatabase";
import { isLikelyVideoUrl, pickBestStillImageUrl, stillImageCandidatesFromPost } from "../../../Utils/media/feedMedia";
import { useEdit, E } from "./InlineEdit";
import { useTemplateAnalyticsOptional } from "./TemplateAnalyticsContext";
import type { ShopCatalogProduct } from "./templateShopCatalog";
import {
  fetchTemplatePlatformPostsByIds,
  orderTemplatePlatformPostsByHighlightIds,
  templatePlatformPostsQueryKey,
} from "./templatePlatformPostsQuery";
import { useQuery } from "react-query";
import {
  TemplateMarqueeTrack,
  shouldMarqueeTicker,
} from "./TemplateMarqueeTrack";

export type BlogRelatedView = "carousel" | "marquee";

export type BlogRelatedRailItem = {
  id: string;
  title: string;
  subtitle: string;
  pill: string;
  image: string;
  href?: string;
  kind: "content" | "product";
};

function postCoverImage(row: Record<string, unknown>): string {
  return (
    pickBestStillImageUrl(stillImageCandidatesFromPost(row)) ||
    pickBestStillImageUrl([
      row.thumbnail,
      row.thumbnail_url,
      row.table_image,
      row.card_image,
      row.image,
      row.product_image,
      row.storage_url,
      row.media_url,
    ])
  );
}

function postTitle(row: Record<string, unknown>): string {
  const caption = String(
    row.content_title ?? row.caption ?? row.title ?? row.name ?? "",
  ).trim();
  if (!caption) return "";
  return caption.split("\n")[0]?.slice(0, 80) || "";
}

function formatPostDate(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function contentPill(row: Record<string, unknown>): string {
  const type = String(row.type ?? "").trim().toUpperCase();
  const platform = String(row.platform ?? "").trim().toLowerCase();
  if (
    type === "REELS" ||
    type === "TIKTOK" ||
    type === "VIDEO" ||
    type === "STORY" ||
    platform === "tiktok" ||
    platform === "youtube"
  ) {
    return "Watch";
  }
  if (type === "PODCAST" || platform === "podcast") return "Listen";
  return "Read";
}

export function mapRelatedContentPostsToRailItems(
  posts: unknown[],
  orderedIds: string[],
): BlogRelatedRailItem[] {
  const ordered = orderTemplatePlatformPostsByHighlightIds(posts, orderedIds);
  const out: BlogRelatedRailItem[] = [];
  for (const raw of ordered) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const id = String(row.post_id ?? row.id ?? "").trim();
    if (!id) continue;
    const href = String(
      row.permalink ?? row.url ?? row.external_link ?? row.media_url ?? "",
    )
      .split(",")[0]
      ?.trim();
    out.push({
      id,
      title: postTitle(row) || "Untitled",
      subtitle: formatPostDate(row.post_date) || contentPill(row),
      pill: contentPill(row),
      image: postCoverImage(row),
      href: href && !isLikelyVideoUrl(href) ? href : undefined,
      kind: "content",
    });
  }
  return out;
}

export function mapRelatedProductsToRailItems(
  products: ShopCatalogProduct[],
): BlogRelatedRailItem[] {
  return products.map((p) => ({
    id: p.postId,
    title: p.name || "Product",
    subtitle: p.breadcrumbs?.brand || "Shop",
    pill: "Shop",
    image: p.image,
    href: p.url && p.url !== "#" ? p.url : undefined,
    kind: "product" as const,
  }));
}

function BlogRelatedCard({
  item,
  userId,
  variant,
}: {
  item: BlogRelatedRailItem;
  userId: string;
  variant: BlogRelatedView;
}) {
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const interactive = Boolean(item.href) && !editable;

  const onActivate = () => {
    if (!interactive || !item.href) return;
    if (userId && item.id) {
      if (item.kind === "product") {
        analytics?.trackShopClick({
          postId: item.id,
          name: item.title,
          sectionId: "relatedContent",
          shopView: "category",
          contentCategory: item.subtitle,
        });
      } else {
        analytics?.trackPostClick({
          postId: item.id,
          mediaUrl: item.image,
          name: item.title,
          title: item.title,
          sectionId: "relatedContent",
          sectionType: "RelatedContent",
        });
      }
      void updateRefClick(item.id, userId);
    }
    window.open(item.href, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={`mte-tpl-blog-rail-item mte-tpl-blog-rail-item--${variant}${
        interactive ? " mte-tpl-blog-rail-item--interactive" : ""
      }`}
      role={interactive ? "link" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onActivate();
              }
            }
          : undefined
      }
    >
      <div className="mte-tpl-blog-rail-thumb">
        {item.image ? (
          <img
            src={item.image}
            alt=""
            className="mte-tpl-img-filter mte-tpl-blog-rail-thumb-img"
          />
        ) : null}
        {item.pill ? (
          <span className="mte-tpl-blog-rail-pill">{item.pill}</span>
        ) : null}
      </div>
      <div className="mte-tpl-blog-rail-cap">
        {item.title ? (
          <h4 className="mte-tpl-blog-rail-cap-title">
            <E path={`related.${item.kind}.${item.id}.title`}>{item.title}</E>
          </h4>
        ) : null}
        {item.subtitle ? (
          <div className="mte-tpl-blog-rail-cap-sub">
            <E path={`related.${item.kind}.${item.id}.subtitle`}>
              {item.subtitle}
            </E>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function BlogRelatedRail({
  items,
  view,
  userId,
  loading,
}: {
  items: BlogRelatedRailItem[];
  view: BlogRelatedView;
  userId: string;
  loading?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  if (loading && items.length === 0) {
    return (
      <div className="mte-tpl-blog-rail-skel" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="mte-tpl-blog-rail-skel-card" />
        ))}
      </div>
    );
  }

  if (!items.length) return null;

  if (view === "marquee") {
    const ticker = shouldMarqueeTicker(items.length);
    const loop = ticker ? items : [...items, ...items];
    return (
      <TemplateMarqueeTrack
        mode={ticker ? "ticker" : "loop"}
        outerClassName="mte-tpl-blog-marquee-shell"
        trackClassName="mte-tpl-blog-marquee-track mte-tpl-blog-marquee-track--js"
      >
        {loop.map((it, i) => (
          <BlogRelatedCard
            key={`${it.id}-${i}`}
            item={it}
            userId={userId}
            variant="marquee"
          />
        ))}
      </TemplateMarqueeTrack>
    );
  }

  const scrollBy = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(280, el.clientWidth * 0.7), behavior: "smooth" });
  };

  const showArrows = items.length > 1;
  return (
    <div className="mte-tpl-blog-carousel-shell">
      {showArrows ? (
      <button
        type="button"
        className="mte-tpl-blog-car-btn mte-tpl-blog-car-btn--prev"
        aria-label="Previous"
        onClick={() => scrollBy(-1)}
      >
        ‹
      </button>
      ) : null}
      <div className="mte-tpl-blog-carousel" ref={trackRef}>
        {items.map((it) => (
          <BlogRelatedCard
            key={it.id}
            item={it}
            userId={userId}
            variant="carousel"
          />
        ))}
      </div>
      {showArrows ? (
      <button
        type="button"
        className="mte-tpl-blog-car-btn mte-tpl-blog-car-btn--next"
        aria-label="Next"
        onClick={() => scrollBy(1)}
      >
        ›
      </button>
      ) : null}
    </div>
  );
}

/** Fetch + map related content posts for the blog rail. */
export function useRelatedContentRailItems(args: {
  userId: string;
  username: string;
  postIds: string[];
  enabled: boolean;
}) {
  const { userId, username, postIds, enabled } = args;
  const query = useQuery(
    templatePlatformPostsQueryKey(userId, username, "related-content", postIds),
    () =>
      fetchTemplatePlatformPostsByIds({
        userId,
        username,
        platformKey: "related-content",
        postIds,
        maxPosts: 24,
      }),
    {
      enabled: enabled && Boolean(userId) && Boolean(username) && postIds.length > 0,
    },
  );
  const items = React.useMemo(
    () =>
      mapRelatedContentPostsToRailItems(query.data ?? [], postIds),
    [query.data, postIds],
  );
  return {
    items,
    loading: Boolean(query.isLoading || query.isFetching),
  };
}
